import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@material-ui/core'
import { useAppContext } from '../../../redux/slices/appContext'
import SydActionMenu from '../../commonDesign/SydActionMenu'
import ArchiveReportModal from '../../pages/admin/StatusReports/components/StatusReportModals/ArchiveReportModal'
import FinalizeReportModal from '../../pages/admin/StatusReports/components/StatusReportModals/FinalizeReportModal'
import CreateWealthJourneyEntryModal
  from '../../pages/admin/StatusReports/components/StatusReportModals/CreateWealthJourneyEntryModal'
import { useCheckPolicy } from '../../../hooks'
import { useStatusReportContext } from './statusReportContext'
import StatusReportSummaryModal from './StatusReportSummaryModal'

// Available action keys that can be enabled
const AVAILABLE_ACTIONS = {
  FINALIZE: 'finalize',
  ARCHIVE: 'archive',
  SUMMARY: 'summary',
  WEALTH_JOURNEY: 'wealth-journey-entry'
}

function StatusReportActionsMenu ({
  enabledActions,
  size,
  icon,
  className,
  style
}) {
  const { clientId } = useAppContext()
  const { report: statusReport, isLoading } = useStatusReportContext()
  const [showModal, setShowModal] = useState(null)

  const canEditMilestones = useCheckPolicy('editMilestones')
  const canEditStatusReport = useCheckPolicy('status_report_edit')

  const actions = useMemo(() => {
    if (!statusReport?.statusReportId) return []

    const allActions = [
      {
        key: AVAILABLE_ACTIONS.FINALIZE,
        label: 'Finalize This Report',
        icon: 'layer',
        onClick: () => setShowModal({
          mode: 'finalize',
          statusReportId: statusReport.statusReportId,
          statusTemplateId: statusReport.statusTemplateId,
          levelId: clientId
        }),
        disabled: statusReport.status !== 'active'
      },
      {
        key: AVAILABLE_ACTIONS.ARCHIVE,
        label: 'Archive This Report',
        icon: 'lock',
        onClick: () => setShowModal({
          mode: 'archive',
          statusReportId: statusReport.statusReportId
        }),
        disabled: statusReport.status === 'archived'
      },
      {
        key: AVAILABLE_ACTIONS.SUMMARY,
        label: 'Get Summary',
        icon: 'document',
        onClick: () => setShowModal({
          mode: 'summary',
          statusReportId: statusReport.statusReportId
        }),
        disabled: false
      },
      {
        key: AVAILABLE_ACTIONS.WEALTH_JOURNEY,
        label: 'Create Wealth Journey Entry',
        icon: 'calendar',
        onClick: () => setShowModal({
          mode: 'wealth-journey-entry',
          statusReportId: statusReport.statusReportId
        }),
        disabled: !canEditMilestones
      }
    ]

    // Filter to only enabled actions
    return allActions.filter(action =>
      enabledActions.includes(action.key) && !action.disabled
    )
  }, [statusReport, enabledActions, clientId, canEditMilestones, setShowModal])

  const handleCloseModal = () => {
    setShowModal(null)
  }

  if (isLoading) {
    return (
      <div className='p-2'>
        <CircularProgress size={18} />
      </div>
    )
  }

  if (!statusReport || !canEditStatusReport || !actions?.length) {
    return null
  }

  return (
    <>
      <SydActionMenu
        actions={actions}
        subject={statusReport}
        size={size}
        icon={icon}
        className={className}
        style={style}
      />

      {/* Modals */}
      {showModal?.mode === 'finalize' && (
        <FinalizeReportModal
          open
          onClose={handleCloseModal}
          statusTemplateId={showModal.statusTemplateId}
          levelId={showModal.levelId}
          onSuccess={(data) => {
            if (canEditMilestones) {
              setShowModal({
                statusReportId: data.statusReportId,
                mode: 'wealth-journey-entry'
              })
            } else {
              handleCloseModal()
            }
          }}
        />
      )}

      {showModal?.mode === 'archive' && (
        <ArchiveReportModal
          open
          onClose={handleCloseModal}
          statusReportId={showModal.statusReportId}
        />
      )}

      {showModal?.mode === 'summary' && (
        <StatusReportSummaryModal
          open
          onClose={handleCloseModal}
          statusReportId={showModal.statusReportId}
        />
      )}

      {showModal?.mode === 'wealth-journey-entry' && (
        <CreateWealthJourneyEntryModal
          open
          onClose={handleCloseModal}
          statusReportId={showModal.statusReportId}
        />
      )}
    </>
  )
}

StatusReportActionsMenu.propTypes = {
  enabledActions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AVAILABLE_ACTIONS))),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object
}

StatusReportActionsMenu.defaultProps = {
  enabledActions: [
    AVAILABLE_ACTIONS.FINALIZE,
    AVAILABLE_ACTIONS.ARCHIVE,
    AVAILABLE_ACTIONS.SUMMARY,
    AVAILABLE_ACTIONS.WEALTH_JOURNEY
  ]
}

export { AVAILABLE_ACTIONS }
export default StatusReportActionsMenu
